import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt, fmtB, fmtDate } from '@dcc-erp/shared';
import type {
  CashflowCategoryCode,
  CashflowCategoryRow,
  CashflowForecastResponse,
  CreateManualForecastEntry,
  CashflowScenarioResponse,
  CashflowVarianceResponse,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useAddManualForecastEntry,
  useCashflowForecast,
  useCashflowScenario,
  useCashflowVariance,
  useRunCashflowForecast,
} from '../lib/queries';

// ============================================================
// Category catalog (spec §31) — kept local to the route so the route file is
// self-contained for label/tone rendering. The codes are the authoritative
// contract with the API.
// ============================================================

type CfFlow = 'in' | 'out';

interface CfCategory {
  code: CashflowCategoryCode;
  label: string;
  type: CfFlow;
  tone: string;
}

const CF_CATEGORIES: readonly CfCategory[] = [
  { code: 'AR_COL', label: 'รับชำระจากลูกหนี้', type: 'in', tone: '#117a3d' },
  { code: 'CASH_SALE', label: 'ขายเงินสด', type: 'in', tone: '#0e7490' },
  { code: 'OTHER_IN', label: 'รายได้อื่น', type: 'in', tone: '#1a56a6' },
  { code: 'AP_PAY', label: 'จ่ายเจ้าหนี้', type: 'out', tone: '#b42318' },
  { code: 'PAYROLL', label: 'เงินเดือนพนักงาน', type: 'out', tone: '#b54708' },
  { code: 'UTILITY', label: 'ค่าสาธารณูปโภค', type: 'out', tone: '#a16207' },
  { code: 'TAX', label: 'ภาษีและเงินสมทบ', type: 'out', tone: '#7030A0' },
  { code: 'CAPEX', label: 'ลงทุนสินทรัพย์', type: 'out', tone: '#52525b' },
  { code: 'LOAN', label: 'ชำระเงินกู้ + ดอกเบี้ย', type: 'out', tone: '#0369a1' },
];

type TabKey = 'weekly' | 'category' | 'scenario' | 'variance';

// Threshold for the "low cash" alert banner (per spec §31).
const MIN_BAL_THRESHOLD = 200_000;

// Spec §31 scenario presets — operator can override via inline number inputs.
interface ScenarioPreset {
  key: 'best' | 'base' | 'worst';
  label: string;
  assumption: string;
  rev: number;
  exp: number;
  tone: 'positive' | 'warning' | 'negative';
  risk: string;
}
const SCENARIO_PRESETS: readonly ScenarioPreset[] = [
  {
    key: 'best',
    label: 'Best Case (+20% / -5%)',
    assumption: 'เก็บหนี้เร็ว · ขายเงินสดเพิ่ม 20% · ค่าใช้จ่ายลด 5%',
    rev: 1.2,
    exp: 0.95,
    tone: 'positive',
    risk: 'ต่ำ',
  },
  {
    key: 'base',
    label: 'Base Case',
    assumption: 'ตามแผนปัจจุบัน',
    rev: 1.0,
    exp: 1.0,
    tone: 'warning',
    risk: 'ปานกลาง',
  },
  {
    key: 'worst',
    label: 'Worst Case (-20% / +5%)',
    assumption: 'เก็บหนี้ช้า · ขายลดลง 20% · ค่าใช้จ่ายเพิ่ม 5%',
    rev: 0.8,
    exp: 1.05,
    tone: 'negative',
    risk: 'สูง · ต้องกู้',
  },
];

function num(v: string | number): number {
  return typeof v === 'string' ? Number(v) : v;
}

export const Route = createFileRoute('/cfforecast')({ component: CfForecastPage });

function CfForecastPage() {
  const [tab, setTab] = useState<TabKey>('weekly');
  const companyId = useActiveCompanyId();
  // Default weeks=13; baseDate is omitted to let the API pick the latest run.
  const forecast = useCashflowForecast(companyId, { weeks: 13 });
  const runForecast = useRunCashflowForecast(companyId);

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['weekly', 'Weekly Forecast (13 สัปดาห์)'],
    ['category', 'แยกตามหมวด Cash In/Out'],
    ['scenario', 'Scenario Analysis'],
    ['variance', 'Forecast vs Actual'],
  ];

  const data = forecast.data;
  const openBal = data ? num(data.openingCash) : 0;
  const totalIn = data ? num(data.totalProjectedInflow) : 0;
  const totalOut = data ? num(data.totalProjectedOutflow) : 0;
  const endBal = data ? num(data.closingCash) : 0;
  const minBal = data ? num(data.minClosingCash) : 0;
  const minBalWeek = data?.minClosingCashWeek ?? 1;

  // Surface the low-cash alert when ANY week dips below the threshold (matches
  // prototype behaviour — uses the API's pre-computed minClosingCash).
  const alertWeek = data && minBal < MIN_BAL_THRESHOLD ? data.weeks[minBalWeek - 1] : null;

  return (
    <div className="page">
      <PageHead
        crumbs={['ภาพรวม', 'Cashflow Forecast']}
        title="คาดการณ์เงินสดรับ-จ่าย 13 สัปดาห์ข้างหน้า"
        actions={
          <>
            <HelpButton guide="cfforecast" />
            <select className="select" defaultValue="13w">
              <option value="13w">13 สัปดาห์</option>
              <option value="6m">6 เดือน</option>
              <option value="12m">12 เดือน</option>
            </select>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire export */
              }}
            >
              <Icon name="download" size={14} /> Export
            </button>
            <button
              className="btn primary"
              disabled={runForecast.isPending || !companyId}
              onClick={() => {
                runForecast.mutate({ weeksAhead: 13 });
              }}
            >
              <Icon name="refresh" size={14} />{' '}
              {runForecast.isPending ? 'กำลังคำนวณ…' : 'Refresh Forecast'}
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            เงินสดต้นงวด
          </div>
          <div className="value tnum">
            {fmtB(openBal)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{data ? `ณ ${fmtDate(data.baseDate)}` : '—'}</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            เงินสดรับ 13W
          </div>
          <div className="value tnum" style={{ color: 'var(--positive)' }}>
            +{fmtB(totalIn)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">เฉลี่ย {fmt(Math.round(totalIn / 13))} /สัปดาห์</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            เงินสดจ่าย 13W
          </div>
          <div className="value tnum" style={{ color: 'var(--negative)' }}>
            −{fmtB(totalOut)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">เฉลี่ย {fmt(Math.round(totalOut / 13))} /สัปดาห์</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            เงินสดปลายงวด
          </div>
          <div className="value tnum">
            {fmtB(endBal)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            ต่ำสุด {fmtB(minBal)} (W{minBalWeek})
          </div>
        </div>
      </div>

      {alertWeek && (
        <div
          style={{
            padding: '14px 18px',
            background: '#fef3e8',
            border: '1px solid #f0dab0',
            borderLeft: '4px solid var(--warning)',
            borderRadius: 8,
            marginBottom: 'var(--section-gap)',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'var(--warning)',
              color: 'white',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
            }}
          >
            !
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>
              คำเตือน: เงินสดอาจต่ำกว่า Min Threshold ในสัปดาห์ W{alertWeek.weekNumber} (
              {fmtDate(alertWeek.weekStartDate)})
            </div>
            <div className="meta" style={{ marginTop: 2 }}>
              เงินสดคงเหลือคาดการณ์: {fmtB(num(alertWeek.closeBalance))} ·
              ควรพิจารณาเร่งเก็บหนี้หรือเลื่อนการจ่าย
            </div>
          </div>
        </div>
      )}

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {forecast.isPending && (
        <div className="card" style={{ padding: 24 }}>
          <span className="muted">กำลังโหลด…</span>
        </div>
      )}
      {forecast.isError && (
        <div className="card" style={{ padding: 24, color: 'var(--negative)' }}>
          โหลดล้มเหลว: {forecast.error.message} — ลองกด &quot;Refresh Forecast&quot; เพื่อสร้าง
          snapshot ใหม่
        </div>
      )}
      {!forecast.isPending &&
        !forecast.isError &&
        !data &&
        (tab === 'weekly' || tab === 'category') && (
          <div
            className="card"
            style={{
              padding: 32,
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 14,
            }}
          >
            ยังไม่มี Cashflow Forecast snapshot — กดปุ่ม &quot;<b>Refresh Forecast</b>&quot;
            เพื่อสร้างรายการแรก (13 สัปดาห์ข้างหน้า)
          </div>
        )}
      {data && tab === 'weekly' && <WeeklyView data={data} companyId={companyId} />}
      {data && tab === 'category' && <CategoryView data={data} />}
      {tab === 'scenario' && (
        <ScenarioView companyId={companyId} baseDate={data?.baseDate} hasBase={!!data} />
      )}
      {tab === 'variance' && <VarianceView companyId={companyId} />}
    </div>
  );
}

function WeeklyView({
  data,
  companyId,
}: {
  data: CashflowForecastResponse;
  companyId: string | undefined;
}) {
  const summary = data.weeks;
  const rowsByCat = useMemo(() => {
    const map = new Map<CashflowCategoryCode, CashflowCategoryRow>();
    for (const r of data.rows) map.set(r.category, r);
    return map;
  }, [data.rows]);

  const max = Math.max(...summary.map((w) => num(w.closeBalance)), 1);
  const ins = CF_CATEGORIES.filter((c) => c.type === 'in');
  const outs = CF_CATEGORIES.filter((c) => c.type === 'out');

  const addManual = useAddManualForecastEntry(companyId);

  // Cell value lookup — signed Decimal string straight from the API matrix.
  function cellValue(code: CashflowCategoryCode, weekIdx: number): number {
    const row = rowsByCat.get(code);
    if (!row) return 0;
    return Math.abs(num(row.amounts[weekIdx] ?? 0));
  }
  function catTotal(code: CashflowCategoryCode): number {
    const row = rowsByCat.get(code);
    if (!row) return 0;
    return Math.abs(num(row.total));
  }

  function onAddManual() {
    if (!companyId) return;
    const cat = window.prompt(
      'Category (AR_COL/CASH_SALE/OTHER_IN/AP_PAY/PAYROLL/UTILITY/TAX/CAPEX/LOAN):',
    );
    if (!cat || !CF_CATEGORIES.some((c) => c.code === cat)) return;
    const wkStr = window.prompt('Week number (1-13)?');
    const wk = Number(wkStr);
    if (!wk || wk < 1 || wk > summary.length) return;
    const amtStr = window.prompt('Amount (signed — negative for outflow):');
    const amt = Number(amtStr);
    if (Number.isNaN(amt)) return;
    const desc = window.prompt('Description:') ?? 'Manual adjustment';
    const wkRow = summary[wk - 1]!;
    const payload: CreateManualForecastEntry = {
      baseDate: new Date(data.baseDate).toISOString().slice(0, 10),
      weekStartDate: new Date(wkRow.weekStartDate).toISOString().slice(0, 10),
      category: cat as CashflowCategoryCode,
      amount: amt,
      description: desc,
    };
    addManual.mutate(payload);
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <h3>กราฟเงินสดคงเหลือ 13 สัปดาห์</h3>
          <button className="btn small" onClick={onAddManual} disabled={addManual.isPending}>
            <Icon name="plus" size={12} /> Add manual entry
          </button>
        </div>
        <div className="card-body">
          <div className="bars" style={{ height: 200 }}>
            {summary.map((w) => {
              const close = num(w.closeBalance);
              const h = max > 0 ? (close / max) * 100 : 0;
              const low = close < MIN_BAL_THRESHOLD;
              return (
                <div className="bcol" key={w.weekNumber}>
                  <div className="bstack">
                    <div
                      className="b1"
                      style={{
                        height: `${Math.max(h, 0)}%`,
                        background: low ? 'var(--negative)' : 'var(--warning)',
                      }}
                    />
                  </div>
                  <div className="blabel">W{w.weekNumber}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="tbl-wrap" style={{ maxHeight: 560, overflowX: 'auto' }}>
          <table className="tbl" style={{ minWidth: 1700 }}>
            <thead>
              <tr>
                <th
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: '#fafafa',
                    zIndex: 2,
                    minWidth: 220,
                  }}
                >
                  รายการ
                </th>
                {summary.map((w) => (
                  <th key={w.weekNumber} className="num">
                    W{w.weekNumber}
                    <div className="meta">{fmtDate(w.weekStartDate)}</div>
                  </th>
                ))}
                <th className="num">รวม</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ background: '#fafafa', fontWeight: 600 }}>
                <td
                  style={{ position: 'sticky', left: 0, background: '#fafafa', zIndex: 1 }}
                  colSpan={summary.length + 2}
                >
                  เงินสดรับ
                </td>
              </tr>
              {ins.map((c) => (
                <tr key={c.code}>
                  <td style={{ position: 'sticky', left: 0, background: 'white', zIndex: 1 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: c.tone }} />
                      {c.label}
                    </span>
                  </td>
                  {summary.map((_, i) => {
                    const v = cellValue(c.code, i);
                    return (
                      <td key={i} className="num tnum">
                        {v ? fmt(v) : '—'}
                      </td>
                    );
                  })}
                  <td className="num tnum">
                    <b>{fmt(catTotal(c.code))}</b>
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#e7f4ec', fontWeight: 600 }}>
                <td style={{ position: 'sticky', left: 0, background: '#e7f4ec', zIndex: 1 }}>
                  รวมเงินสดรับ
                </td>
                {summary.map((w) => (
                  <td key={w.weekNumber} className="num tnum">
                    {fmt(num(w.inflow))}
                  </td>
                ))}
                <td className="num tnum">{fmt(num(data.totalProjectedInflow))}</td>
              </tr>

              <tr style={{ background: '#fafafa', fontWeight: 600 }}>
                <td
                  style={{ position: 'sticky', left: 0, background: '#fafafa', zIndex: 1 }}
                  colSpan={summary.length + 2}
                >
                  เงินสดจ่าย
                </td>
              </tr>
              {outs.map((c) => (
                <tr key={c.code}>
                  <td style={{ position: 'sticky', left: 0, background: 'white', zIndex: 1 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: c.tone }} />
                      {c.label}
                    </span>
                  </td>
                  {summary.map((_, i) => {
                    const v = cellValue(c.code, i);
                    return (
                      <td key={i} className="num tnum">
                        {v ? '(' + fmt(v) + ')' : '—'}
                      </td>
                    );
                  })}
                  <td className="num tnum">
                    <b>({fmt(catTotal(c.code))})</b>
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#fce9e7', fontWeight: 600 }}>
                <td style={{ position: 'sticky', left: 0, background: '#fce9e7', zIndex: 1 }}>
                  รวมเงินสดจ่าย
                </td>
                {summary.map((w) => (
                  <td key={w.weekNumber} className="num tnum">
                    ({fmt(num(w.outflow))})
                  </td>
                ))}
                <td className="num tnum">({fmt(num(data.totalProjectedOutflow))})</td>
              </tr>

              <tr className="total">
                <td style={{ position: 'sticky', left: 0, background: '#f4f4f7', zIndex: 1 }}>
                  Net Cashflow
                </td>
                {summary.map((w) => {
                  const net = num(w.net);
                  return (
                    <td
                      key={w.weekNumber}
                      className="num tnum"
                      style={{ color: net >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                    >
                      {net >= 0 ? '+' : ''}
                      {fmt(net)}
                    </td>
                  );
                })}
                <td className="num tnum">
                  {fmt(num(data.totalProjectedInflow) - num(data.totalProjectedOutflow))}
                </td>
              </tr>
              <tr style={{ background: 'var(--brand-tint)', fontWeight: 700 }}>
                <td
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: 'var(--brand-tint)',
                    zIndex: 1,
                  }}
                >
                  เงินสดคงเหลือปลายสัปดาห์
                </td>
                {summary.map((w) => {
                  const close = num(w.closeBalance);
                  return (
                    <td
                      key={w.weekNumber}
                      className="num tnum"
                      style={{
                        color:
                          close < MIN_BAL_THRESHOLD
                            ? 'var(--negative)'
                            : close < 250_000
                              ? 'var(--warning)'
                              : 'var(--brand)',
                      }}
                    >
                      {fmt(close)}
                    </td>
                  );
                })}
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function CategoryView({ data }: { data: CashflowForecastResponse }) {
  const rowsByCat = new Map<string, CashflowCategoryRow>();
  for (const r of data.rows) rowsByCat.set(r.category, r);

  const ins = CF_CATEGORIES.filter((c) => c.type === 'in').map((c) => ({
    ...c,
    total: Math.abs(num(rowsByCat.get(c.code)?.total ?? 0)),
  }));
  const totalIn = ins.reduce((s, c) => s + c.total, 0) || 1;
  const outs = CF_CATEGORIES.filter((c) => c.type === 'out').map((c) => ({
    ...c,
    total: Math.abs(num(rowsByCat.get(c.code)?.total ?? 0)),
  }));
  const totalOut = outs.reduce((s, c) => s + c.total, 0) || 1;

  return (
    <div className="grid-2-eq">
      <div className="card">
        <div className="card-head">
          <h3>เงินสดรับตามแหล่งที่มา</h3>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>หมวด</th>
                <th className="num">13 สัปดาห์รวม</th>
                <th className="num">เฉลี่ย/สัปดาห์</th>
                <th className="num">%</th>
              </tr>
            </thead>
            <tbody>
              {ins.map((c) => (
                <tr key={c.code}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{ width: 10, height: 10, borderRadius: 2, background: c.tone }}
                      />
                      {c.label}
                    </span>
                  </td>
                  <td className="num tnum">{fmt(c.total)}</td>
                  <td className="num tnum">{fmt(Math.round(c.total / 13))}</td>
                  <td className="num">{((c.total / totalIn) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card">
        <div className="card-head">
          <h3>เงินสดจ่ายตามวัตถุประสงค์</h3>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>หมวด</th>
                <th className="num">13 สัปดาห์รวม</th>
                <th className="num">เฉลี่ย/สัปดาห์</th>
                <th className="num">%</th>
              </tr>
            </thead>
            <tbody>
              {outs.map((c) => (
                <tr key={c.code}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{ width: 10, height: 10, borderRadius: 2, background: c.tone }}
                      />
                      {c.label}
                    </span>
                  </td>
                  <td className="num tnum">{fmt(c.total)}</td>
                  <td className="num tnum">{fmt(Math.round(c.total / 13))}</td>
                  <td className="num">{((c.total / totalOut) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Scenario view — live: pulls 3 scenarios in parallel (best/base/worst) and
// lets operator override the multipliers per row. Preserves prototype DOM:
// card-head + table.tbl with same column set (Scenario / สมมติฐาน / รับ 13W /
// จ่าย 13W / เงินสดปลายงวด / ความเสี่ยง).
// ============================================================
function ScenarioView({
  companyId,
  baseDate,
  hasBase,
}: {
  companyId: string | undefined;
  baseDate: Date | string | undefined;
  hasBase: boolean;
}) {
  const baseDateStr = baseDate ? new Date(baseDate).toISOString().slice(0, 10) : undefined;
  // Custom multipliers — start at preset values, operator can edit.
  const [overrides, setOverrides] = useState<Record<string, { rev: number; exp: number }>>(() => {
    const seed: Record<string, { rev: number; exp: number }> = {};
    for (const p of SCENARIO_PRESETS) seed[p.key] = { rev: p.rev, exp: p.exp };
    return seed;
  });

  // When no base forecast exists, the scenario hooks are still mounted (so
  // hook order is stable) but are disabled implicitly because companyId may
  // be undefined or because the API returns nothing without a base run.
  const bestQ = useCashflowScenario(companyId, {
    ...(baseDateStr ? { baseDate: baseDateStr } : {}),
    weeks: 13,
    revMultiplier: overrides.best!.rev,
    expMultiplier: overrides.best!.exp,
  });
  const baseQ = useCashflowScenario(companyId, {
    ...(baseDateStr ? { baseDate: baseDateStr } : {}),
    weeks: 13,
    revMultiplier: overrides.base!.rev,
    expMultiplier: overrides.base!.exp,
  });
  const worstQ = useCashflowScenario(companyId, {
    ...(baseDateStr ? { baseDate: baseDateStr } : {}),
    weeks: 13,
    revMultiplier: overrides.worst!.rev,
    expMultiplier: overrides.worst!.exp,
  });

  const byKey: Record<string, ReturnType<typeof useCashflowScenario>> = {
    best: bestQ,
    base: baseQ,
    worst: worstQ,
  };

  function setOverride(key: string, field: 'rev' | 'exp', val: number) {
    setOverrides((prev) => ({ ...prev, [key]: { ...prev[key]!, [field]: val } }));
  }

  if (!hasBase) {
    return (
      <div
        className="card"
        style={{
          padding: 32,
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: 14,
        }}
      >
        ยังไม่มีข้อมูลสำหรับช่วงเวลานี้ — รัน forecast ก่อน แล้วกลับมาทดสอบ Scenario
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Scenario Analysis — 3 สถานการณ์</h3>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>Scenario</th>
              <th>สมมติฐาน</th>
              <th className="num">รับ 13W</th>
              <th className="num">จ่าย 13W</th>
              <th className="num">เงินสดปลายงวด</th>
              <th>ความเสี่ยง</th>
            </tr>
          </thead>
          <tbody>
            {SCENARIO_PRESETS.map((p) => {
              const q = byKey[p.key]!;
              const d = q.data as CashflowScenarioResponse | undefined;
              const inT = d ? num(d.totalProjectedInflow) : 0;
              const outT = d ? num(d.totalProjectedOutflow) : 0;
              const endT = d ? num(d.closingCash) : 0;
              const rowBg = p.key === 'base' ? 'var(--brand-tint)' : undefined;
              const endColor =
                p.tone === 'positive'
                  ? 'var(--positive)'
                  : p.tone === 'negative'
                    ? 'var(--negative)'
                    : 'var(--brand)';
              return (
                <tr key={p.key} style={rowBg ? { background: rowBg } : undefined}>
                  <td>
                    <b>{p.label}</b>
                    <div className="meta" style={{ marginTop: 4, display: 'flex', gap: 6 }}>
                      <label style={{ fontSize: 11 }}>
                        rev
                        <input
                          type="number"
                          step="0.05"
                          min={0}
                          max={5}
                          value={overrides[p.key]!.rev}
                          onChange={(e) => setOverride(p.key, 'rev', Number(e.target.value) || 0)}
                          style={{ width: 56, marginLeft: 4 }}
                        />
                      </label>
                      <label style={{ fontSize: 11 }}>
                        exp
                        <input
                          type="number"
                          step="0.05"
                          min={0}
                          max={5}
                          value={overrides[p.key]!.exp}
                          onChange={(e) => setOverride(p.key, 'exp', Number(e.target.value) || 0)}
                          style={{ width: 56, marginLeft: 4 }}
                        />
                      </label>
                    </div>
                  </td>
                  <td>{p.assumption}</td>
                  <td
                    className={`num tnum ${p.tone === 'positive' ? 'pos' : p.tone === 'negative' ? 'neg' : ''}`}
                  >
                    {q.isPending ? '…' : fmt(inT)}
                  </td>
                  <td className="num tnum">{q.isPending ? '…' : fmt(outT)}</td>
                  <td className="num tnum" style={{ color: endColor, fontWeight: 700 }}>
                    {q.isPending ? '…' : fmt(endT)}
                  </td>
                  <td>
                    <span className={`badge ${p.tone}`}>{p.risk}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div
        style={{
          padding: '14px 18px',
          background: '#fafafa',
          borderTop: '1px solid var(--border)',
          fontSize: 14,
          lineHeight: 1.55,
        }}
      >
        💡 <b>ข้อเสนอแนะ:</b> ปรับ rev/exp multiplier ในแต่ละแถวเพื่อทดสอบสมมติฐานเพิ่มเติม —
        MANUAL/OTHER rows ไม่ถูกสเกล (เป็น cashflow ที่ทราบแน่นอน)
      </div>
    </div>
  );
}

// ============================================================
// Variance view — live: GET /variance?lookbackWeeks=4.
// Actual = posted JE on cash COA 1110-1119 (debit=in, credit=out).
// Forecast = closest CashflowForecastRun whose horizon covers the week.
// ============================================================
function VarianceView({ companyId }: { companyId: string | undefined }) {
  const [lookback] = useState(4);
  const q = useCashflowVariance(companyId, { lookbackWeeks: lookback });
  const data = q.data as CashflowVarianceResponse | undefined;

  return (
    <div className="card">
      <div className="card-head">
        <h3>
          Forecast vs Actual — {lookback} สัปดาห์ย้อนหลัง
          {data ? ` (avg accuracy ${data.summary.avgHitRate.toFixed(1)}%)` : ''}
        </h3>
      </div>
      <div className="card-body flush">
        {q.isPending && <div style={{ padding: 24 }}>กำลังโหลด…</div>}
        {q.isError && (
          <div style={{ padding: 24 }}>ไม่สามารถโหลดข้อมูล Variance ได้ — ลองรีเฟรชหน้า</div>
        )}
        {data && (
          <table className="tbl">
            <thead>
              <tr>
                <th>สัปดาห์</th>
                <th className="num">Forecast In</th>
                <th className="num">Actual In</th>
                <th className="num">Variance In</th>
                <th className="num">Forecast Out</th>
                <th className="num">Actual Out</th>
                <th className="num">Variance Out</th>
                <th>Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {data.weeks.map((w) => {
                const fIn = num(w.forecast.totalInflow);
                const fOut = num(w.forecast.totalOutflow);
                const aIn = num(w.actual.totalInflow);
                const aOut = num(w.actual.totalOutflow);
                const vIn = num(w.variance.inflow);
                const vOut = num(w.variance.outflow);
                const acc = w.variance.hitRate;
                return (
                  <tr key={w.weekLabel}>
                    <td>
                      <b>{w.weekLabel}</b>
                      {w.forecast.runId === null && (
                        <div className="meta" style={{ marginTop: 2 }}>
                          ไม่มี forecast run ที่ครอบคลุมสัปดาห์นี้
                        </div>
                      )}
                    </td>
                    <td className="num tnum">{fmt(fIn)}</td>
                    <td className="num tnum">{fmt(aIn)}</td>
                    <td
                      className="num tnum"
                      style={{ color: vIn >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                    >
                      {vIn >= 0 ? '+' : ''}
                      {fmt(vIn)}
                    </td>
                    <td className="num tnum">{fmt(fOut)}</td>
                    <td className="num tnum">{fmt(aOut)}</td>
                    <td
                      className="num tnum"
                      style={{ color: vOut > 0 ? 'var(--negative)' : 'var(--positive)' }}
                    >
                      {vOut >= 0 ? '+' : ''}
                      {fmt(vOut)}
                    </td>
                    <td>
                      <span
                        className={`badge ${acc >= 95 ? 'positive' : acc >= 90 ? 'warning' : 'negative'}`}
                      >
                        {acc.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
              {data.weeks.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>
                    ยังไม่มีข้อมูลในช่วงเวลาที่เลือก
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {data && data.weeks.length > 0 && (
        <div
          style={{
            padding: '14px 18px',
            background: '#fafafa',
            borderTop: '1px solid var(--border)',
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          📊 <b>สรุป:</b> สัปดาห์ที่แม่นที่สุด: {data.summary.bestWeek || '—'} ·
          ที่คลาดเคลื่อนที่สุด: {data.summary.worstWeek || '—'}
        </div>
      )}
    </div>
  );
}
